import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';

const COLORS = ['#4ADE80', '#FCD34D', '#38BDF8', '#F472B6', '#C084FC', '#A3E635', '#FF8A65'];

export default function LiveCanvas({ profileId }) {
  const [remoteCursors, setRemoteCursors] = useState({});
  const channelRef = useRef(null);
  const [myCursorId] = useState(() => `visitor-${Math.random().toString(36).substring(2, 9)}`);
  const [myColor] = useState(() => COLORS[Math.floor(Math.random() * COLORS.length)]);

  useEffect(() => {
    if (!profileId) return;

    // Create a Supabase channel for this specific profile
    const room = supabase.channel(`profile:${profileId}`, {
      config: { presence: { key: myCursorId } }
    });
    channelRef.current = room;

    room
      .on('presence', { event: 'sync' }, () => {
        const newState = room.presenceState();
        const cursors = {};
        for (const id in newState) {
          if (id !== myCursorId) {
            cursors[id] = newState[id][0]; // latest state
          }
        }
        setRemoteCursors(cursors);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        if (key !== myCursorId) {
          setRemoteCursors(prev => ({ ...prev, [key]: newPresences[0] }));
        }
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        if (key !== myCursorId) {
          setRemoteCursors(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Send an initial invisible presence to register the visitor count
          await room.track({
            x: -100,
            y: -100,
            color: myColor,
          });
        }
      });

    return () => {
      room.unsubscribe();
    };
  }, [profileId, myCursorId, myColor]);

  useEffect(() => {
    let ticking = false;
    let lastSent = Date.now();
    
    const handlePointerMove = (e) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Throttle updates to ~15fps (every 66ms) to avoid killing the WebSocket connection
          if (Date.now() - lastSent > 66) {
            if (channelRef.current && channelRef.current.state === 'joined') {
              channelRef.current.track({
                x: e.pageX,
                y: e.pageY,
                color: myColor,
              });
              lastSent = Date.now();
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 z-[100] overflow-hidden min-h-screen">
      <AnimatePresence>
        {Object.entries(remoteCursors).map(([id, cursor]) => {
          if (!cursor.x || cursor.x < 0) return null;
          return (
            <motion.div
              key={id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1, x: cursor.x, y: cursor.y }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300, mass: 0.5 }}
              className="absolute top-0 left-0"
              style={{
                width: '16px',
                height: '16px',
                marginLeft: '-8px',
                marginTop: '-8px',
              }}
            >
              {/* Bioluminescent Pulse / Firefly */}
              <div 
                className="w-full h-full rounded-full absolute animate-pulse" 
                style={{ backgroundColor: cursor.color || '#4ADE80', filter: 'blur(8px)', opacity: 0.8 }} 
              />
              <div 
                className="w-2 h-2 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" 
                style={{ backgroundColor: cursor.color || '#4ADE80', boxShadow: `0 0 10px ${cursor.color || '#4ADE80'}` }} 
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
